//@author: devin

import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    forwardRef,
    inject,
    Input,
    input,
    NgZone,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
    ViewRef,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { ItemService } from "../../../util/items/item.service";
import { S25DropdownAbstract } from "../s25.dropdown.abstract";
import { SearchCriteriaType } from "../../../pojo/SearchCriteriaI";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { DropdownDataUtil } from "../dropdown.data.util";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { SearchCriteriaContext } from "../../../services/search/search-criteria/search.criteria.context";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { jSith } from "../../../util/jquery-replacement";

@TypeManagerDecorator("s25-ng-dropdown-search-criteria")
@Component({
    selector: "s25-ng-dropdown-search-criteria",
    template: `
        @if (items) {
            <s25-generic-dropdown
                [value]="value()"
                [hasFav]="hasFav"
                [onSelect]="onSelect"
                [items]="items"
                [staticItems]="staticItems"
                [autoOpen]="autoOpen"
                [alwaysOpen]="alwaysOpen"
                [hideSearch]="hideSearch"
                [focusFirst]="false"
                [searchEnabled]="searchEnabled"
                [apiBean]="apiBean"
                [serverSide]="serverSide"
                [placeholder]="placeholder || defaultPlaceholder"
                [resetSelectedOnCleanup]="resetSelectedOnCleanup"
                [popoverClass]="popoverClass"
                [useChoiceForChosen]="useChoiceForChosen"
                [onLoad]="onLoad"
                [noSelection]="noSelectionUsePlaceholder ? placeholder || defaultPlaceholder : noSelection"
                [filter]="filter"
                [emptyText]="emptyText"
                [renderItem]="renderItem && type !== 'eventRequirements'"
                [hasNoneItem]="hasNoneItem"
                [noneItemLabel]="noneItemLabel"
                [readOnly]="readOnly"
                [blockDetailsNav]="blockDetailsNav"
                [fieldId]="fieldId()"
                [ariaLabel]="ariaLabel"
                (valueChange)="onValueChange($event)"
            />
        }
    `,
    styles: [
        `
            /*
             * Call sites often add class "c-input" for form row alignment, but the real field chrome
             * comes from the inner Select2 (.select2-choice). Without this reset, .c-input draws a
             * second border and padding around the dropdown ("double border").
             */
            s25-ng-dropdown-search-criteria.c-input {
                background: transparent !important;
                border: none !important;
                box-shadow: none !important;
                height: auto !important;
                min-height: 0 !important;
                padding: 0 !important;
            }
            s25-ng-dropdown-search-criteria.c-input:hover,
            s25-ng-dropdown-search-criteria.c-input:focus,
            s25-ng-dropdown-search-criteria.c-input:focus-within {
                background: transparent !important;
                border: none !important;
                box-shadow: none !important;
                outline: none;
            }
            s25-ng-dropdown-search-criteria.c-input .ngDropdownPaginated,
            s25-ng-dropdown-search-criteria.c-input .select2-container {
                box-sizing: border-box;
                min-width: 100%;
                width: 100%;
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => S25DropdownSearchCriteriaComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class S25DropdownSearchCriteriaComponent extends S25DropdownAbstract implements OnInit, OnChanges {
    @Input() type?: SearchCriteriaType["type"];
    @Input() popoverClass?: string;
    @Input() placeholder: string;
    @Input() useChoiceForChosen: boolean;
    @Input() noSelectionUsePlaceholder: boolean;
    @Input() renderItem: boolean = true;
    /** When set, overrides {@link SearchCriteriaContextI.expensiveSearch} for this instance. */
    @Input() expensiveSearchOverride?: boolean;
    readonly fieldId = input<string>();

    private searchCriteriaContext = inject(SearchCriteriaContext);
    readonly itemService = inject(ItemService);

    defaultPlaceholder: string; // Used if no placeholder is provided

    static MIN_ITEMS_FOR_SEARCH: number = 10;

    constructor(
        private zone: NgZone,
        private elementRef: ElementRef,
    ) {
        super();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.type && !changes.type.isFirstChange()) {
            this.staticItems = [];
            this.ngOnInit();
        }
    }

    ngOnInit() {
        if (!this.type) return;
        const context = this.searchCriteriaContext.Context[this.type];
        if (context) {
            this.defaultPlaceholder = "Select from " + context.title;
            this.hasFav = S25Util.toBool(context.showFavorite);

            const value = this.value();
            if (value && !value.itemTypeId) {
                value.itemTypeId = context.itemTypeId;
            }

            let p: Promise<any>;
            let DDItems: DropDownItem[] = [];

            const expensiveSearch = this.expensiveSearchOverride ?? context.expensiveSearch;

            if (expensiveSearch) {
                this.searchEnabled = true;
                p = this.itemService
                    .criteria(this.type)
                    .fetchAll({
                        params: {
                            cacheId: -999,
                            pageNum: 0,
                            pageSize: 999,
                            filterFavorites: true,
                            customFilterValue: this.customFilterValue,
                        },
                    })
                    .then((data: any) => {
                        this.zone.run(() => {
                            this.staticItems = this.staticItems || (value ? [value] : []);
                            const staticCopy = [].concat(this.staticItems);
                            const favorites = SearchCriteriaService.extractItems(data);

                            jSith.forEach(favorites, (favKey, fav) => {
                                let inStatic = false;

                                jSith.forEach(
                                    staticCopy,
                                    (itemKey, item) => {
                                        if (parseInt(fav.itemId) === parseInt(item.itemId)) {
                                            inStatic = true;
                                            Object.assign(item, { ...fav, ...item });
                                            return true;
                                        }
                                    },
                                    false,
                                    true,
                                );

                                if (!inStatic) {
                                    if (staticCopy.length && staticCopy[0].grp) {
                                        fav.grp = "Starred";
                                    }
                                    this.staticItems.push(fav);
                                }
                            });

                            this.serverSide = DropdownDataUtil.getServerSideModel(
                                context.serviceMethod,
                                this.elementRef.nativeElement,
                                this.customFilterValue,
                            );
                            DDItems = S25Util.deepCopy(this.staticItems);
                        });
                    });
            } else {
                //if useSecurity is set, list of event types is filtered to those allowed by rose config and preference config (used for writing new event type)
                p = this.itemService
                    .criteria(this.type)
                    .fetchAll({
                        params: {
                            useSecurity: S25Util.toBool(this.useSecurity),
                            activeOnly: S25Util.toBool(this.activeOnly),
                            omitReadOnly: S25Util.toBool(this.omitReadOnly),
                            customFilterValue: this.customFilterValue,
                        },
                    })
                    .then((data: DropDownItem[]) => {
                        this.zone.run(() => {
                            const statics = S25Util.deepCopy(this.staticItems);
                            const items = SearchCriteriaService.extractItems(data);
                            this.staticItems = items.filter((obj: DropDownItem) => {
                                return obj.favorite === "T";
                            });
                            statics && statics.length > 0 ? this.staticItems.unshift(statics[0]) : "";
                            DDItems = items;
                            this.searchEnabled =
                                DDItems.length > S25DropdownSearchCriteriaComponent.MIN_ITEMS_FOR_SEARCH;
                        });
                    });
            }

            p.then(() => {
                this.items = DDItems;
                this.cd && !(this.cd as ViewRef).destroyed && this.cd.detectChanges();
            });
        }
    }
}
